import React from 'react';
import { act } from 'react-dom/test-utils';
import { render, waitFor } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import { accessFixture, accountFixture, userFixture } from '../fixtures';
import { LOCKS, NOTICES } from '../components/AccessPermissions/copy';
import useAccessPermissionsForm from './useAccessPermissionsForm';

let hook;

const renderHook = (user, options) => {
  function Probe() {
    hook = useAccessPermissionsForm(user, options);

    return null;
  }

  return render(
    <ConfigProvider>
      <Probe />
    </ConfigProvider>,
  );
};

const EDITABLE = { canEdit: true };

const withAccounts = (accounts, accessOverrides = {}) =>
  userFixture({
    access: accessFixture({
      teams: [{ id: 'all', name: 'All Teams', accounts }],
      ...accessOverrides,
    }),
  });

const rows = () => hook.groups.flatMap((group) => group.rows);
const row = (id) => rows().find((candidate) => candidate.id === id);
const cell = (rowId, key) =>
  row(rowId).cells.find((candidate) => candidate.key === key);

const set = (rowId, key, value) => act(() => hook.setCell(rowId, key, value));
const mode = (next) => act(() => hook.setMode(next));

const FLEET_ADMIN = 'fleet-administrator';
const REPORT = 'report-user';
const GROUP = 'management-role';
const TIER = 'management-role:tier';
const EXTERNAL = 'visibleAsExternalRecipient';

const secondAccount = (overrides = {}) =>
  accountFixture({ accountId: 5222, name: 'Account Name B', ...overrides });

describe('useAccessPermissionsForm', () => {
  describe('availability', () => {
    it('is unavailable when the server sent no access payload', () => {
      renderHook(userFixture(), EDITABLE);

      expect(hook.isAvailable).toBe(false);
    });

    it('is unavailable when the user holds no role in any segment account', () => {
      renderHook(withAccounts([]), EDITABLE);

      expect(hook.isAvailable).toBe(false);
    });

    it('is available once there is a team to draw', () => {
      renderHook(withAccounts([accountFixture()]), EDITABLE);

      expect(hook.isAvailable).toBe(true);
    });
  });

  describe('toggling roles', () => {
    it('adds a role without disturbing the ones already held', () => {
      renderHook(withAccounts([accountFixture()]), EDITABLE);

      set('4711', REPORT, true);

      expect(cell('4711', FLEET_ADMIN).checked).toBe(true);
      expect(cell('4711', REPORT).checked).toBe(true);
    });

    it('removes only the role unchecked', () => {
      renderHook(
        withAccounts([
          accountFixture({ roleIds: ['role-fleet-admin', 'role-report'] }),
        ]),
        EDITABLE,
      );

      set('4711', REPORT, false);

      expect(cell('4711', FLEET_ADMIN).checked).toBe(true);
      expect(cell('4711', REPORT).checked).toBe(false);
    });

    it('grants the group its first tier when the group is checked', () => {
      renderHook(withAccounts([accountFixture()]), EDITABLE);

      expect(cell('4711', TIER).value).toBeNull();

      set('4711', GROUP, true);

      expect(cell('4711', GROUP).checked).toBe(true);
      expect(cell('4711', TIER).value).toEqual({
        label: 'Member',
        roleId: 'role-member',
      });
    });

    it('clears the tier when the group is unchecked', () => {
      renderHook(
        withAccounts([
          accountFixture({ roleIds: ['role-fleet-admin', 'role-manager'] }),
        ]),
        EDITABLE,
      );

      set('4711', GROUP, false);

      expect(cell('4711', GROUP).checked).toBe(false);
      expect(cell('4711', TIER).value).toBeNull();
      expect(cell('4711', FLEET_ADMIN).checked).toBe(true);
    });

    it('replaces within the group when a tier is chosen, and nothing else', () => {
      renderHook(
        withAccounts([
          accountFixture({ roleIds: ['role-fleet-admin', 'role-member'] }),
        ]),
        EDITABLE,
      );

      set('4711', TIER, 'role-manager');

      expect(cell('4711', TIER).value.label).toBe('Manager');
      expect(cell('4711', FLEET_ADMIN).checked).toBe(true);
      expect(hook.rolesFor(4711)).toEqual(['Fleet Administrator', 'Manager']);
    });

    it('keeps a role it has no column for', () => {
      renderHook(
        withAccounts([
          accountFixture({ roleIds: ['role-fleet-admin', 'role-private'] }),
        ]),
        EDITABLE,
      );

      set('4711', REPORT, true);

      expect(hook.isValid).toBe(true);
      expect(cell('4711', FLEET_ADMIN).checked).toBe(true);
    });
  });

  describe('validity', () => {
    it('is invalid while any visible account has no role', () => {
      renderHook(withAccounts([accountFixture()]), EDITABLE);

      set('4711', FLEET_ADMIN, false);

      expect(hook.isValid).toBe(false);
      expect(hook.canSave).toBe(false);
      expect(row('4711').invalid).toBe(true);
    });

    it('needs a change as well as validity before it can save', () => {
      renderHook(withAccounts([accountFixture()]), EDITABLE);

      expect(hook.isValid).toBe(true);
      expect(hook.canSave).toBe(false);

      set('4711', REPORT, true);

      expect(hook.canSave).toBe(true);
    });
  });

  describe('the last role', () => {
    it('cannot be unchecked, and says where to go instead', () => {
      renderHook(withAccounts([accountFixture()]), EDITABLE);

      expect(cell('4711', FLEET_ADMIN).disabled).toBe(true);
      expect(cell('4711', FLEET_ADMIN).lockReason).toBe(LOCKS.lastRole);
    });

    it('unlocks as soon as a second role is held', () => {
      renderHook(
        withAccounts([
          accountFixture({ roleIds: ['role-fleet-admin', 'role-report'] }),
        ]),
        EDITABLE,
      );

      expect(cell('4711', FLEET_ADMIN).disabled).toBe(false);
      expect(cell('4711', FLEET_ADMIN).lockReason).toBeNull();
    });

    it('leaves the tier switchable, since swapping keeps the count at one', () => {
      renderHook(
        withAccounts([accountFixture({ roleIds: ['role-member'] })]),
        EDITABLE,
      );

      expect(cell('4711', GROUP).disabled).toBe(true);
      expect(cell('4711', TIER).disabled).toBe(false);
    });

    it('does not fire when the only role is one the table cannot draw', () => {
      renderHook(
        withAccounts([
          accountFixture({ roleIds: ['role-fleet-admin', 'role-private'] }),
        ]),
        EDITABLE,
      );

      expect(cell('4711', FLEET_ADMIN).disabled).toBe(false);
    });
  });

  describe('editing your own record', () => {
    const selfUser = () =>
      withAccounts(
        [accountFixture({ roleIds: ['role-fleet-admin', 'role-report'] })],
        {
          isSelf: true,
        },
      );

    it('locks every role control with the exact sentence', () => {
      renderHook(selfUser(), EDITABLE);

      [FLEET_ADMIN, REPORT, GROUP, TIER].forEach((key) => {
        expect(cell('4711', key).disabled).toBe(true);
        expect(cell('4711', key).lockReason).toBe(LOCKS.selfModify);
      });
    });

    it('leaves discoverability editable — it is not an escalation', () => {
      renderHook(selfUser(), EDITABLE);

      expect(cell('4711', EXTERNAL).disabled).toBe(false);
      expect(cell('4711', EXTERNAL).lockReason).toBeNull();
    });
  });

  describe('a read-only viewer', () => {
    it('disables everything without a reason on each control', () => {
      renderHook(withAccounts([accountFixture()]), { canEdit: false });

      expect(cell('4711', REPORT).disabled).toBe(true);
      expect(cell('4711', REPORT).lockReason).toBeNull();
      expect(cell('4711', EXTERNAL).disabled).toBe(true);
    });
  });

  describe('scope', () => {
    it('hides the choice for a single account and forces custom', () => {
      renderHook(withAccounts([accountFixture()]), EDITABLE);

      expect(hook.showScopeChoice).toBe(false);
      expect(hook.mode).toBe('custom');
    });

    it('opens on Apply to All when every account already agrees', () => {
      renderHook(withAccounts([accountFixture(), secondAccount()]), EDITABLE);

      expect(hook.showScopeChoice).toBe(true);
      expect(hook.mode).toBe('all');
      expect(rows()).toHaveLength(1);
      expect(row('all').name).toBe('All Accounts');
    });

    it('opens on Custom when they disagree', () => {
      renderHook(
        withAccounts([
          accountFixture(),
          secondAccount({ roleIds: ['role-report'] }),
        ]),
        EDITABLE,
      );

      expect(hook.mode).toBe('custom');
      expect(rows()).toHaveLength(2);
    });

    it('keeps the agreed value when switching to Apply to All', () => {
      renderHook(
        withAccounts([
          accountFixture({ roleIds: ['role-report'] }),
          secondAccount({ roleIds: ['role-report'] }),
        ]),
        EDITABLE,
      );

      mode('custom');
      mode('all');

      expect(cell('all', REPORT).checked).toBe(true);
      expect(hook.notice).toBeNull();
    });

    it('blanks the row and explains itself when they disagree', () => {
      renderHook(
        withAccounts([
          accountFixture(),
          secondAccount({ roleIds: ['role-report'] }),
        ]),
        EDITABLE,
      );

      mode('all');

      expect(cell('all', FLEET_ADMIN).checked).toBe(false);
      expect(cell('all', REPORT).checked).toBe(false);
      expect(hook.notice).toBe(NOTICES.overwrite);
      expect(hook.isValid).toBe(false);
      expect(hook.canSave).toBe(false);
    });

    it('applies the shared value to every account on the way back', () => {
      renderHook(
        withAccounts([
          accountFixture(),
          secondAccount({ roleIds: ['role-report'] }),
        ]),
        EDITABLE,
      );

      mode('all');
      set('all', REPORT, true);
      mode('custom');

      expect(cell('4711', REPORT).checked).toBe(true);
      expect(cell('5222', REPORT).checked).toBe(true);
      expect(cell('4711', FLEET_ADMIN).checked).toBe(false);
    });

    it('leaves the rows alone when the shared row was never edited', () => {
      renderHook(
        withAccounts([
          accountFixture(),
          secondAccount({ roleIds: ['role-report'] }),
        ]),
        EDITABLE,
      );

      mode('all');
      mode('custom');

      expect(cell('4711', FLEET_ADMIN).checked).toBe(true);
      expect(cell('5222', REPORT).checked).toBe(true);
      expect(hook.canSave).toBe(false);
    });

    it('is not a change in itself', () => {
      renderHook(withAccounts([accountFixture(), secondAccount()]), EDITABLE);

      mode('custom');
      mode('all');

      expect(hook.canSave).toBe(false);
    });
  });

  describe('the header tags', () => {
    it('follow the draft rather than the last saved record', () => {
      renderHook(withAccounts([accountFixture()]), EDITABLE);

      expect(hook.permissionAssignments).toEqual([
        {
          accountId: '4711',
          account: 'Account Name A',
          permission: 'Fleet Administrator',
        },
      ]);

      set('4711', REPORT, true);

      expect(hook.permissionAssignments).toEqual([
        {
          accountId: '4711',
          account: 'Account Name A',
          permission: 'Fleet Administrator',
        },
        {
          accountId: '4711',
          account: 'Account Name A',
          permission: 'Report User',
        },
      ]);
    });

    it('tag a held role the matrix has no column for', () => {
      renderHook(
        withAccounts([
          accountFixture({
            roleIds: ['role-fleet-admin', 'role-custom-a'],
            unlistedRoleNames: { 'role-custom-a': 'Global Administrator' },
          }),
        ]),
        EDITABLE,
      );

      expect(hook.permissionAssignments.map((a) => a.permission)).toEqual([
        'Fleet Administrator',
        'Global Administrator',
      ]);
    });

    it('drop its tag when the role is not held', () => {
      renderHook(
        withAccounts([
          accountFixture({
            roleIds: ['role-fleet-admin'],
            unlistedRoleNames: { 'role-custom-a': 'Global Administrator' },
          }),
        ]),
        EDITABLE,
      );

      expect(hook.permissionAssignments.map((a) => a.permission)).toEqual([
        'Fleet Administrator',
      ]);
    });
  });

  describe('saving', () => {
    const respondWith = ({ ok = true, status = 200, body = {} }) => {
      global.fetch = jest.fn().mockResolvedValue({
        ok,
        status,
        json: async () => body,
      });
    };

    beforeEach(() => {
      document.head.innerHTML =
        '<meta name="csrf-token" content="test-token" />';
    });

    afterEach(() => {
      delete global.fetch;
    });

    const startSave = () =>
      act(() => {
        hook.save();
      });

    it("sends one entry per visible account, in the server's vocabulary", async () => {
      renderHook(withAccounts([accountFixture()]), EDITABLE);
      respondWith({ body: { access: accessFixture() } });

      set('4711', REPORT, true);
      startSave();
      await waitFor(() => expect(global.fetch).toHaveBeenCalled());

      const [url, request] = global.fetch.mock.calls[0];
      expect(url).toBe('/management/users/7/access_permissions');
      expect(request.method).toBe('PATCH');
      expect(JSON.parse(request.body)).toEqual({
        accounts: [
          {
            account_id: 4711,
            role_ids: ['role-fleet-admin', 'role-report'],
            discoverability: {
              visible_as_external_recipient: true,
              visible_as_internal_recipient: true,
            },
          },
        ],
      });
    });

    it('maps a self-modification refusal onto the sentence the tooltip uses', async () => {
      renderHook(withAccounts([accountFixture()]), EDITABLE);
      respondWith({
        ok: false,
        status: 422,
        body: {
          errors: { 'accounts.4711': ['Modifications are not permitted'] },
          errorCodes: { 'accounts.4711': ['decisiv:account_users:003'] },
        },
      });

      set('4711', REPORT, true);
      startSave();

      await waitFor(() =>
        expect(hook.errors.byAccount['4711']).toEqual([LOCKS.selfModify]),
      );
    });

    it('keeps a cell toggled while the request was in flight', async () => {
      renderHook(
        withAccounts([
          accountFixture({ roleIds: ['role-fleet-admin', 'role-report'] }),
        ]),
        EDITABLE,
      );

      let release;
      global.fetch = jest.fn().mockReturnValue(
        new Promise((resolve) => {
          release = () =>
            resolve({
              ok: true,
              status: 200,
              json: async () => ({
                access: accessFixture({
                  teams: [
                    {
                      id: 'all',
                      name: 'All Teams',
                      accounts: [
                        accountFixture({ roleIds: ['role-fleet-admin'] }),
                      ],
                    },
                  ],
                }),
              }),
            });
        }),
      );

      set('4711', REPORT, false);
      act(() => {
        hook.save();
      });

      set('4711', GROUP, true);

      act(() => {
        release();
      });

      await waitFor(() => expect(hook.isSaving).toBe(false));

      expect(cell('4711', GROUP).checked).toBe(true);
    });

    it('keeps a cell put back to its saved value while the request was in flight', async () => {
      renderHook(
        withAccounts([
          accountFixture({ roleIds: ['role-fleet-admin', 'role-report'] }),
        ]),
        EDITABLE,
      );

      let release;
      global.fetch = jest.fn().mockReturnValue(
        new Promise((resolve) => {
          release = () =>
            resolve({
              ok: true,
              status: 200,
              json: async () => ({
                access: accessFixture({
                  teams: [
                    {
                      id: 'all',
                      name: 'All Teams',
                      accounts: [
                        accountFixture({ roleIds: ['role-fleet-admin'] }),
                      ],
                    },
                  ],
                }),
              }),
            });
        }),
      );

      set('4711', REPORT, false);
      act(() => {
        hook.save();
      });
      set('4711', REPORT, true);

      act(() => {
        release();
      });
      await waitFor(() => expect(hook.isSaving).toBe(false));

      expect(cell('4711', REPORT).checked).toBe(true);
    });

    it('keeps an edit made after switching to Custom mid-flight', async () => {
      renderHook(withAccounts([accountFixture(), secondAccount()]), EDITABLE);

      let release;
      global.fetch = jest.fn().mockReturnValue(
        new Promise((resolve) => {
          release = () =>
            resolve({
              ok: true,
              status: 200,
              json: async () => ({
                access: accessFixture({
                  teams: [
                    {
                      id: 'all',
                      name: 'All Teams',
                      accounts: [
                        accountFixture({
                          roleIds: ['role-fleet-admin', 'role-report'],
                        }),
                        secondAccount({
                          roleIds: ['role-fleet-admin', 'role-report'],
                        }),
                      ],
                    },
                  ],
                }),
              }),
            });
        }),
      );

      mode('all');
      set('all', REPORT, true);
      act(() => {
        hook.save();
      });

      mode('custom');
      set('4711', REPORT, false);

      act(() => {
        release();
      });
      await waitFor(() => expect(hook.isSaving).toBe(false));

      expect(cell('4711', REPORT).checked).toBe(false);
      expect(cell('5222', REPORT).checked).toBe(true);
    });

    it('does not resurrect pre-save rows after an Apply to All save', async () => {
      renderHook(withAccounts([accountFixture(), secondAccount()]), EDITABLE);
      const saved = [
        accountFixture({ roleIds: ['role-fleet-admin', 'role-report'] }),
        secondAccount({ roleIds: ['role-fleet-admin', 'role-report'] }),
      ];
      respondWith({
        body: {
          access: accessFixture({
            teams: [{ id: 'all', name: 'All Teams', accounts: saved }],
          }),
        },
      });

      mode('all');
      set('all', REPORT, true);
      startSave();
      await waitFor(() => expect(hook.isSaving).toBe(false));

      mode('custom');

      expect(cell('4711', REPORT).checked).toBe(true);
      expect(cell('5222', REPORT).checked).toBe(true);
      expect(hook.canSave).toBe(false);
    });

    it('names the Apply to All group the way the server named the team', () => {
      renderHook(
        withAccounts([accountFixture(), secondAccount()], {
          teams: [
            {
              id: 'all',
              name: 'Todos los equipos',
              accounts: [accountFixture(), secondAccount()],
            },
          ],
        }),
        EDITABLE,
      );

      mode('all');

      expect(hook.groups[0].name).toBe('Todos los equipos');
    });

    it('carries the real account ids on the Apply to All row', () => {
      renderHook(withAccounts([accountFixture(), secondAccount()]), EDITABLE);

      mode('all');

      expect(row('all').accountIds).toEqual(['4711', '5222']);
    });

    it('clears an Apply to All refusal when the row is edited', async () => {
      renderHook(withAccounts([accountFixture(), secondAccount()]), EDITABLE);
      respondWith({
        ok: false,
        status: 422,
        body: {
          errors: { 'accounts.4711': ['Modifications are not permitted'] },
          errorCodes: { 'accounts.4711': ['decisiv:account_users:003'] },
        },
      });

      mode('all');
      set('all', REPORT, true);
      startSave();

      await waitFor(() =>
        expect(hook.errors.byAccount['4711']).toEqual([LOCKS.selfModify]),
      );

      set('all', REPORT, false);

      expect(hook.errors.byAccount['4711']).toBeUndefined();
    });
  });

  describe('roles the table has no column for', () => {
    const withCustom = () =>
      withAccounts([
        accountFixture({ roleIds: ['role-fleet-admin', 'role-custom-a'] }),
        secondAccount({ roleIds: ['role-report'] }),
      ]);

    it('does not flag the Apply to All row red for an account that holds one', () => {
      renderHook(
        withAccounts([
          accountFixture({ roleIds: ['role-custom-a'] }),
          secondAccount({ roleIds: ['role-custom-b'] }),
        ]),
        EDITABLE,
      );

      mode('all');

      expect(row('all').invalid).toBe(false);
      expect(hook.isValid).toBe(true);
    });

    it('does flag it when a column-less role is not there to save the row', () => {
      renderHook(withAccounts([accountFixture(), secondAccount()]), EDITABLE);

      mode('all');
      set('all', FLEET_ADMIN, false);

      expect(row('all').invalid).toBe(true);
      expect(hook.isValid).toBe(false);
    });

    it('does not count them when deciding whether the accounts agree', () => {
      renderHook(
        withAccounts([
          accountFixture({ roleIds: ['role-fleet-admin', 'role-custom-a'] }),
          secondAccount({ roleIds: ['role-fleet-admin'] }),
        ]),
        EDITABLE,
      );

      expect(hook.mode).toBe('all');
      expect(row('all').cells.find((c) => c.key === FLEET_ADMIN).checked).toBe(
        true,
      );
    });

    it('keeps each account its own through an Apply to All save', async () => {
      renderHook(withCustom(), EDITABLE);
      respondWith({ body: { access: accessFixture() } });

      mode('all');
      set('all', REPORT, true);
      startSave();
      await waitFor(() => expect(global.fetch).toHaveBeenCalled());

      const { accounts } = JSON.parse(global.fetch.mock.calls[0][1].body);

      expect(accounts).toEqual([
        {
          account_id: 4711,
          role_ids: ['role-custom-a', 'role-report'],
          discoverability: {
            visible_as_external_recipient: true,
            visible_as_internal_recipient: true,
          },
        },
        {
          account_id: 5222,
          role_ids: ['role-report'],
          discoverability: {
            visible_as_external_recipient: true,
            visible_as_internal_recipient: true,
          },
        },
      ]);
    });

    it('keeps them when the admin edits the shared row and switches back', async () => {
      renderHook(withCustom(), EDITABLE);
      respondWith({ body: { access: accessFixture() } });

      mode('all');
      set('all', REPORT, true);
      mode('custom');
      startSave();
      await waitFor(() => expect(global.fetch).toHaveBeenCalled());

      const { accounts } = JSON.parse(global.fetch.mock.calls[0][1].body);

      expect(accounts.find((a) => a.account_id === 4711).role_ids).toEqual([
        'role-custom-a',
        'role-report',
      ]);
    });

    it('keeps them through a round trip that changed nothing', () => {
      renderHook(withCustom(), EDITABLE);

      mode('all');
      mode('custom');

      expect(hook.canSave).toBe(false);
    });

    const respondWith = ({ ok = true, status = 200, body = {} }) => {
      global.fetch = jest.fn().mockResolvedValue({
        ok,
        status,
        json: async () => body,
      });
    };

    const startSave = () =>
      act(() => {
        hook.save();
      });

    beforeEach(() => {
      document.head.innerHTML =
        '<meta name="csrf-token" content="test-token" />';
    });

    afterEach(() => {
      delete global.fetch;
    });
  });
});
